import type { ElevationPoint } from '../types';

export interface ElevationThumbnailOptions {
  width?: number;
  height?: number;
  backgroundColor?: string;
  lineColor?: string | 'gradient';
  fillColor?: string | 'gradient';
  strokeWidth?: number;
  padding?: number;
  showGrid?: boolean;
  gridColor?: string;
  textColor?: string;
  font?: string;
  showLabels?: boolean;
  quality?: number;
}

const DEFAULT_OPTIONS: Required<ElevationThumbnailOptions> = {
  width: 480,  // Much larger for crisp quality
  height: 240,
  backgroundColor: '#ffffff',
  lineColor: '#2563eb',
  fillColor: 'rgba(37, 99, 235, 0.1)',
  strokeWidth: 3,  // Thicker lines for better visibility
  padding: 35, // More padding for better labels
  showGrid: true,
  gridColor: 'rgba(0, 0, 0, 0.1)',
  textColor: '#666666',
  font: '14px Inter, -apple-system, BlinkMacSystemFont, sans-serif', // Larger font
  showLabels: true,
  quality: 1.0 // Maximum quality
};

interface GradientSegment {
  startDistance: number;
  endDistance: number;
  avgSteepness: number;
  offset: number;
}

/**
 * Calculate gradient segments based on elevation changes (matching ElevationProfile.tsx logic)
 * For thumbnails, use one segment per data point for smooth, detailed coloring
 */
function calculateGradientSegments(elevationData: ElevationPoint[]): GradientSegment[] {
  if (elevationData.length < 2) return [];
  
  const segments: GradientSegment[] = [];
  const totalDistance = Math.max(...elevationData.map(p => p.distance || 0));
  
  // For thumbnails: create one segment per elevation point for maximum smoothness
  // This ensures the thumbnail looks identical to the main chart
  for (let i = 0; i < elevationData.length - 1; i++) {
    const current = elevationData[i];
    const next = elevationData[i + 1];
    
    // Use the steepness value from the elevation data if available
    let steepness = current.steepness;
    
    // Fallback calculation if steepness is not available
    if (steepness === undefined || steepness === null) {
      const elevationChange = next.elevation - current.elevation;
      const distanceChange = (next.distance || 0) - (current.distance || 0);
      steepness = distanceChange > 0 ? (elevationChange / (distanceChange * 1000)) * 100 : 0;
    }
    
    // Use segment start position for offset calculation
    const offset = totalDistance > 0 ? ((current.distance || 0) / totalDistance) * 100 : 0;
    
    segments.push({
      startDistance: current.distance || 0,
      endDistance: next.distance || 0,
      avgSteepness: steepness,
      offset: Math.max(0, Math.min(100, offset))
    });
  }
  
  return segments;
}

/**
 * Get color for a given steepness value (matching ElevationProfile.tsx colors exactly)
 * Uses raw steepness (not absolute value) - only colors uphill sections
 */
function getColorForSteepness(steepness: number, opacity: number = 1): string {
  const alpha = opacity === 1 ? '' : `, ${opacity}`;
  
  // Match the main elevation chart thresholds exactly: < 4%, 4-8%, 8-12%, >= 12%
  // Only color positive gradients (uphill) - downhill stays green
  let color = '#4a7c2a'; // Green for downhill or < 4% uphill
  
  if (steepness >= 12) {
    color = '#8B0000'; // Dark red for >= 12% uphill
  } else if (steepness >= 8) {
    color = '#ff0000'; // Red for 8-12% uphill
  } else if (steepness >= 4) {
    color = '#ff9800'; // Orange for 4-8% uphill
  }
  // Negative steepness (downhill) and < 4% keep default green color
  
  return opacity === 1 ? color : `rgba(${
    color === '#8B0000' ? '139, 0, 0' :
    color === '#ff0000' ? '255, 0, 0' :
    color === '#ff9800' ? '255, 152, 0' :
    '74, 124, 42'
  }${alpha})`;
}

/**
 * Create gradient stops from segments
 */
function createGradientStops(segments: GradientSegment[], opacity: number = 1): Array<{offset: number, color: string}> {
  return segments.map(segment => ({
    offset: segment.offset / 100,
    color: getColorForSteepness(segment.avgSteepness, opacity)
  }));
}

export class ElevationThumbnailGenerator {
  /**
   * Generate an elevation profile thumbnail from elevation data
   */
  static generateThumbnail(
    elevationData: ElevationPoint[],
    options: ElevationThumbnailOptions = {}
  ): Promise<Blob> {
    return new Promise((resolve, reject) => {
      try {
        if (!elevationData || elevationData.length === 0) {
          resolve(this.generateEmptyThumbnail(options));
          return;
        }

        const opts = { ...DEFAULT_OPTIONS, ...options };
        
        // Create canvas with high-DPI support for crisp rendering
        const canvas = document.createElement('canvas');
        const pixelRatio = window.devicePixelRatio || 1;
        const scaledWidth = opts.width * pixelRatio;
        const scaledHeight = opts.height * pixelRatio;
        
        canvas.width = scaledWidth;
        canvas.height = scaledHeight;
        canvas.style.width = opts.width + 'px';
        canvas.style.height = opts.height + 'px';
        
        const ctx = canvas.getContext('2d');
        
        if (!ctx) {
          reject(new Error('Canvas 2D context not supported'));
          return;
        }

        // Scale context for high-DPI
        ctx.scale(pixelRatio, pixelRatio);
        
        // Enable high-quality rendering
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';

        // Clear canvas with background color
        ctx.fillStyle = opts.backgroundColor;
        ctx.fillRect(0, 0, opts.width, opts.height);

        // Calculate chart dimensions with extra left padding for Y-axis labels
        const leftPadding = opts.padding + 10; // Extra 10px for Y-axis labels
        const rightPadding = opts.padding;
        const topPadding = opts.padding;
        const bottomPadding = opts.padding + (opts.showLabels ? 15 : 0);
        
        const chartWidth = opts.width - leftPadding - rightPadding;
        const chartHeight = opts.height - topPadding - bottomPadding;

        if (chartWidth <= 0 || chartHeight <= 0) {
          reject(new Error('Chart dimensions too small'));
          return;
        }

        // Calculate elevation bounds with 5% buffer for better visualization
        const elevations = elevationData.map(p => p.elevation).filter(e => !isNaN(e));
        const minElevation = Math.min(...elevations);
        const maxElevation = Math.max(...elevations);
        const elevationRange = Math.max(maxElevation - minElevation, 10); // Minimum 10m range
        const bufferRange = elevationRange * 0.05; // 5% buffer
        const chartMinElevation = minElevation - bufferRange;
        const chartMaxElevation = maxElevation + bufferRange;
        const chartElevationRange = chartMaxElevation - chartMinElevation;

        // Calculate distance bounds
        const distances = elevationData.map(p => p.distance || 0);
        const maxDistance = Math.max(...distances);

        // Draw grid if enabled
        if (opts.showGrid) {
          this.drawGrid(ctx, leftPadding, topPadding, chartWidth, chartHeight, opts);
        }

        // Create path points
        const points = elevationData.map((point, index) => {
          const x = leftPadding + (point.distance || (index / (elevationData.length - 1)) * maxDistance) / maxDistance * chartWidth;
          const y = topPadding + chartHeight - ((point.elevation - chartMinElevation) / chartElevationRange) * chartHeight;
          return { x, y, elevation: point.elevation, distance: point.distance || 0 };
        });

        // Calculate gradient segments for gradient coloring
        const gradientSegments = calculateGradientSegments(elevationData);
        const useGradient = opts.fillColor === 'gradient' || opts.lineColor === 'gradient';

        // Draw filled area
        if (useGradient && gradientSegments.length > 0) {
          // Create area gradient
          const areaGradient = ctx.createLinearGradient(leftPadding, 0, leftPadding + chartWidth, 0);
          const gradientStops = createGradientStops(gradientSegments, 0.3);
          
          gradientStops.forEach(stop => {
            areaGradient.addColorStop(stop.offset, stop.color);
          });
          
          ctx.fillStyle = areaGradient;
        } else {
          ctx.fillStyle = opts.fillColor;
        }
        
        ctx.beginPath();
        ctx.moveTo(points[0].x, topPadding + chartHeight);
        
        // Draw bottom edge to first point
        ctx.lineTo(points[0].x, points[0].y);
        
        // Draw elevation curve
        points.forEach((point, index) => {
          if (index === 0) return;
          ctx.lineTo(point.x, point.y);
        });
        
        // Draw back to bottom edge
        ctx.lineTo(points[points.length - 1].x, topPadding + chartHeight);
        ctx.closePath();
        ctx.fill();

        // Draw elevation line
        if (useGradient && gradientSegments.length > 0) {
          // Create line gradient
          const lineGradient = ctx.createLinearGradient(leftPadding, 0, leftPadding + chartWidth, 0);
          const lineGradientStops = createGradientStops(gradientSegments, 1);
          
          lineGradientStops.forEach(stop => {
            lineGradient.addColorStop(stop.offset, stop.color);
          });
          
          ctx.strokeStyle = lineGradient;
        } else {
          ctx.strokeStyle = opts.lineColor;
        }
        
        ctx.lineWidth = opts.strokeWidth;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        ctx.beginPath();
        ctx.moveTo(points[0].x, points[0].y);
        
        points.forEach((point, index) => {
          if (index === 0) return;
          ctx.lineTo(point.x, point.y);
        });
        
        ctx.stroke();

        // Draw labels if enabled
        if (opts.showLabels) {
          this.drawLabels(ctx, leftPadding, topPadding, minElevation, maxElevation, maxDistance, chartWidth, chartHeight, opts);
        }

        // Convert to blob
        canvas.toBlob(
          (blob) => {
            if (blob) {
              resolve(blob);
            } else {
              reject(new Error('Failed to create blob from canvas'));
            }
          },
          'image/png',
          opts.quality
        );
      } catch (error) {
        reject(error);
      }
    });
  }

  /**
   * Draw grid lines for better readability
   */
  private static drawGrid(
    ctx: CanvasRenderingContext2D,
    leftPadding: number,
    topPadding: number,
    chartWidth: number,
    chartHeight: number,
    opts: Required<ElevationThumbnailOptions>
  ): void {
    ctx.strokeStyle = opts.gridColor;
    ctx.lineWidth = 0.5;
    ctx.setLineDash([2, 2]);

    // Draw vertical grid lines
    for (let i = 1; i < 5; i++) {
      const x = leftPadding + (chartWidth / 5) * i;
      ctx.beginPath();
      ctx.moveTo(x, topPadding);
      ctx.lineTo(x, topPadding + chartHeight);
      ctx.stroke();
    }

    // Draw horizontal grid lines
    for (let i = 1; i < 4; i++) {
      const y = topPadding + (chartHeight / 4) * i;
      ctx.beginPath();
      ctx.moveTo(leftPadding, y);
      ctx.lineTo(leftPadding + chartWidth, y);
      ctx.stroke();
    }

    ctx.setLineDash([]);
  }

  /**
   * Draw elevation and distance labels with axis
   */
  private static drawLabels(
    ctx: CanvasRenderingContext2D,
    leftPadding: number,
    topPadding: number,
    minElevation: number,
    maxElevation: number,
    maxDistance: number,
    chartWidth: number,
    chartHeight: number,
    opts: Required<ElevationThumbnailOptions>
  ): void {
    ctx.fillStyle = opts.textColor;
    ctx.strokeStyle = opts.textColor;
    ctx.font = opts.font;
    ctx.lineWidth = 1;

    // Draw Y-axis (left side - elevation)
    ctx.beginPath();
    ctx.moveTo(leftPadding, topPadding);
    ctx.lineTo(leftPadding, topPadding + chartHeight);
    ctx.stroke();

    // Draw X-axis (bottom - distance)
    ctx.beginPath();
    ctx.moveTo(leftPadding, topPadding + chartHeight);
    ctx.lineTo(leftPadding + chartWidth, topPadding + chartHeight);
    ctx.stroke();

    // Y-axis ticks - match ElevationProfile.tsx exactly
    const elevationBuffer = (maxElevation - minElevation) * 0.1; // 10% buffer like detail panel
    const yAxisMin = Math.floor((minElevation - elevationBuffer) / 10) * 10; // Round to 10m like detail panel
    const yAxisMax = Math.ceil((maxElevation + elevationBuffer) / 10) * 10;   // Round to 10m like detail panel
    
    ctx.textAlign = 'right';
    ctx.textBaseline = 'middle';
    
    // Draw Y-axis ticks - calculate similar to detail panel
    const yRange = yAxisMax - yAxisMin;
    const yNumTicks = Math.min(6, Math.max(3, Math.ceil(yRange / 50))); // Reasonable number of Y ticks
    
    for (let i = 0; i <= yNumTicks; i++) {
      const elevationValue = yAxisMin + (yRange * i / yNumTicks);
      const yPos = topPadding + chartHeight - (elevationValue - yAxisMin) / yRange * chartHeight;
      
      // Draw tick mark
      ctx.beginPath();
      ctx.moveTo(leftPadding - 3, yPos);
      ctx.lineTo(leftPadding, yPos);
      ctx.stroke();
      
      // Draw label - format like detail panel
      ctx.fillText(`${Math.round(elevationValue)}m`, leftPadding - 5, yPos);
    }

    // X-axis ticks - match ElevationProfile.tsx exactly
    const numTicks = 8; // Same as detail panel
    const distanceRange = maxDistance - 0; // Always start from 0
    
    if (distanceRange > 0) {
      // Same step calculation logic as detail panel
      const idealStep = distanceRange / (numTicks - 1);
      let step;
      
      if (idealStep <= 0.5) {
        step = 0.5; // 500m steps for very small ranges
      } else if (idealStep <= 1) {
        step = 1; // 1km steps
      } else if (idealStep <= 2) {
        step = 2; // 2km steps
      } else if (idealStep <= 3) {
        step = 3; // 3km steps
      } else if (idealStep <= 5) {
        step = 5; // 5km steps
      } else {
        step = Math.ceil(idealStep / 5) * 5; // Multiples of 5km, rounded up
      }
      
      // Generate ticks starting from first whole step >= 0
      const startTick = Math.ceil(0 / step) * step;
      
      ctx.textAlign = 'center';
      ctx.textBaseline = 'top';
      
      for (let tick = startTick; tick <= maxDistance; tick += step) {
        if (tick >= 0) {
          const xPos = leftPadding + (tick / maxDistance) * chartWidth;
          
          // Draw tick mark
          ctx.beginPath();
          ctx.moveTo(xPos, topPadding + chartHeight);
          ctx.lineTo(xPos, topPadding + chartHeight + 3);
          ctx.stroke();
          
          // Format label exactly like detail panel
          let tickLabel;
          if (tick % 1 === 0) {
            tickLabel = `${Math.round(tick)}km`;
          } else if (tick % 0.5 === 0) {
            tickLabel = `${tick}km`;
          } else {
            tickLabel = `${Math.round(tick * 10) / 10}km`;
          }
          
          ctx.fillText(tickLabel, xPos, topPadding + chartHeight + 5);
        }
      }
    }
  }

  /**
   * Generate elevation thumbnail with custom styling based on route profile
   */
  static generateThemedThumbnail(
    elevationData: ElevationPoint[],
    _theme: 'road' | 'mtb' | 'gravel' | 'trekking' = 'trekking',
    size: 'small' | 'medium' | 'large' = 'medium'
  ): Promise<Blob> {
    const sizeConfigs = {
      small: { width: 400, height: 200, padding: 30, strokeWidth: 2.5 },
      medium: { width: 600, height: 300, padding: 40, strokeWidth: 3 },
      large: { width: 800, height: 400, padding: 50, strokeWidth: 4 }
    };

    const options: ElevationThumbnailOptions = {
      ...sizeConfigs[size],
      backgroundColor: '#ffffff',
      lineColor: 'gradient',  // Use gradient colors matching detail panel
      fillColor: 'gradient',  // Use gradient colors matching detail panel
      showLabels: true,       // Enable axis and labels
      showGrid: false,        // Keep thumbnails clean like detail panel
      font: size === 'large' ? '16px Inter, -apple-system, BlinkMacSystemFont, sans-serif' : 
            size === 'medium' ? '14px Inter, -apple-system, BlinkMacSystemFont, sans-serif' :
            '12px Inter, -apple-system, BlinkMacSystemFont, sans-serif',
      textColor: '#666666',
      quality: 1.0            // Maximum quality
    };

    return this.generateThumbnail(elevationData, options);
  }

  /**
   * Generate empty thumbnail placeholder
   */
  static generateEmptyThumbnail(options: ElevationThumbnailOptions = {}): Blob {
    const opts = { ...DEFAULT_OPTIONS, ...options };
    
    const canvas = document.createElement('canvas');
    canvas.width = opts.width;
    canvas.height = opts.height;
    const ctx = canvas.getContext('2d')!;

    // Fill background
    ctx.fillStyle = opts.backgroundColor;
    ctx.fillRect(0, 0, opts.width, opts.height);

    // Draw placeholder text
    ctx.fillStyle = opts.textColor;
    ctx.font = opts.font;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('No elevation data', opts.width / 2, opts.height / 2);

    // Convert to blob synchronously
    const dataURL = canvas.toDataURL('image/png', opts.quality);
    const byteString = atob(dataURL.split(',')[1]);
    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);
    
    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
    }
    
    return new Blob([ab], { type: 'image/png' });
  }
}

/**
 * Get elevation thumbnail URL with optional size parameter
 */
export const getElevationThumbnailUrl = (
  baseUrl: string,
  filePath: string,
  size: 'small' | 'medium' | 'large' = 'medium'
): string => {
  const sizeParams = {
    small: 'width=400&height=200&resize=fill&quality=100',
    medium: 'width=600&height=300&resize=fill&quality=100', 
    large: 'width=800&height=400&resize=fill&quality=100'
  };
  
  return `${baseUrl}/storage/v1/object/public/elevation-thumbnails/${filePath}?${sizeParams[size]}`;
};
